import React from 'react';
import { Box, Container, Grid, Paper, Skeleton, Stack } from '@mui/material';
import { BRAND } from '../../theme/brandTheme';

const wave = { animation: 'wave' };

/** Shared KPI card row skeleton */
function KpiRowSkeleton({ count = 4 }) {
  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BRAND.borderLight}`, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="55%" height={40} sx={wave} />
                <Skeleton variant="text" width="70%" height={22} sx={wave} />
                <Skeleton variant="text" width="90%" height={16} sx={{ mt: 0.5, ...wave }} />
              </Box>
              <Skeleton variant="circular" width={40} height={40} sx={wave} />
            </Stack>
            <Skeleton variant="rounded" height={6} sx={{ mt: 2, borderRadius: 3, ...wave }} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

/** Brand overview card grid skeleton */
function BrandGridSkeleton({ count = 6 }) {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: count }).map((_, i) => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BRAND.borderLight}`, borderRadius: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Skeleton variant="rounded" width={40} height={40} sx={wave} />
              <Skeleton variant="text" width="60%" height={28} sx={wave} />
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {[1, 2, 3, 4].map((j) => (
                <Skeleton key={j} variant="rounded" width={72} height={28} sx={{ borderRadius: 1, ...wave }} />
              ))}
            </Stack>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

/** Report list rows skeleton */
function ReportRowsSkeleton({ rows = 6 }) {
  return (
    <Stack spacing={1}>
      {Array.from({ length: rows }).map((_, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            p: 1.5,
            border: `1px solid ${BRAND.borderLight}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={`${60 + (i % 3) * 10}%`} height={22} sx={wave} />
            <Skeleton variant="text" width="40%" height={16} sx={wave} />
          </Box>
          <Skeleton variant="rounded" width={64} height={26} sx={{ borderRadius: 999, ...wave }} />
        </Paper>
      ))}
    </Stack>
  );
}

/** Dashboard overview loading state */
export function DashboardSkeleton() {
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="rounded" width={160} height={28} sx={{ mb: 1.5, borderRadius: 999, ...wave }} />
        <Skeleton variant="text" width="45%" height={40} sx={wave} />
        <Skeleton variant="text" width="70%" height={24} sx={wave} />
      </Box>

      <Skeleton variant="text" width={120} height={28} sx={{ mb: 2, ...wave }} />
      <KpiRowSkeleton count={4} />

      <Paper elevation={0} sx={{ p: 2, mb: 3, border: `1px solid ${BRAND.borderLight}`, borderRadius: 2 }}>
        <Skeleton variant="text" width={140} height={24} sx={{ mb: 1.5, ...wave }} />
        {[1, 2, 3].map((i) => (
          <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Skeleton variant="text" width="30%" height={20} sx={wave} />
            <Skeleton variant="rounded" height={8} sx={{ flex: 1, borderRadius: 4, ...wave }} />
            <Skeleton variant="text" width={40} height={20} sx={wave} />
          </Stack>
        ))}
      </Paper>

      <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mb: 3 }}>
        <Skeleton variant="rounded" width={130} height={36} sx={wave} />
        <Skeleton variant="rounded" width={150} height={36} sx={wave} />
      </Stack>

      <Skeleton variant="text" width={100} height={32} sx={{ mb: 2, ...wave }} />
      <BrandGridSkeleton count={6} />
    </Container>
  );
}

/** Report detail page loading state */
export function ReportDetailSkeleton() {
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Skeleton variant="text" width={80} height={24} sx={wave} />
        <Skeleton variant="text" width={12} height={24} sx={wave} />
        <Skeleton variant="text" width={100} height={24} sx={wave} />
        <Skeleton variant="text" width={12} height={24} sx={wave} />
        <Skeleton variant="text" width="35%" height={24} sx={wave} />
      </Stack>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #eee', borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Skeleton variant="text" width="55%" height={36} sx={wave} />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Skeleton variant="text" width={80} height={22} sx={wave} />
              <Skeleton variant="rounded" width={56} height={24} sx={{ borderRadius: 999, ...wave }} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" width={44} height={44} sx={wave} />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #eee', borderRadius: 2 }}>
        <Skeleton variant="text" width={160} height={24} sx={{ mb: 2, ...wave }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={56} sx={wave} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #eee', borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Skeleton variant="text" width={140} height={26} sx={wave} />
          <Skeleton variant="rounded" width={120} height={32} sx={wave} />
        </Stack>
        <Skeleton variant="text" width="100%" height={18} sx={wave} />
        <Skeleton variant="text" width="85%" height={18} sx={wave} />
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Skeleton variant="rounded" width={100} height={36} sx={wave} />
        <Skeleton variant="rounded" width={100} height={36} sx={wave} />
      </Stack>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
        <Skeleton variant="rounded" height={280} sx={wave} />
      </Paper>
    </Box>
  );
}

/** Single brand page loading state */
export function BrandPageSkeleton() {
  return (
    <Container maxWidth="lg">
      <Skeleton variant="rounded" width={140} height={36} sx={{ mb: 2, ...wave }} />
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Skeleton variant="rounded" width={48} height={48} sx={wave} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={36} sx={wave} />
          <Skeleton variant="text" width="25%" height={22} sx={wave} />
        </Box>
      </Stack>

      <Skeleton variant="text" width={120} height={28} sx={{ mb: 2, ...wave }} />
      <KpiRowSkeleton count={4} />

      <Skeleton variant="text" width={100} height={32} sx={{ mb: 2, mt: 2, ...wave }} />
      <ReportRowsSkeleton rows={8} />
    </Container>
  );
}

/** Statistics page loading state */
export function StatisticsPageSkeleton() {
  return (
    <Container maxWidth="lg">
      <Skeleton variant="rounded" width={140} height={36} sx={{ mb: 2, ...wave }} />
      <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1, ...wave }} />
      <Skeleton variant="text" width="35%" height={24} sx={{ mb: 3, ...wave }} />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BRAND.borderLight}`, borderRadius: 2 }}>
              <Skeleton variant="text" width="50%" height={36} sx={wave} />
              <Skeleton variant="text" width="70%" height={20} sx={wave} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BRAND.borderLight}`, borderRadius: 2 }}>
            <Skeleton variant="circular" width={220} height={220} sx={{ mx: 'auto', ...wave }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BRAND.borderLight}`, borderRadius: 2 }}>
            <Skeleton variant="rounded" height={220} sx={wave} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

/** Trend chart area skeleton */
export function ReportTrendChartSkeleton() {
  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Skeleton variant="text" width={160} height={28} sx={wave} />
        <Skeleton variant="rounded" width={140} height={40} sx={wave} />
      </Stack>
      <Skeleton variant="rounded" height={280} sx={wave} />
    </Paper>
  );
}

/** Table/list loading placeholder for library-style pages */
export function DashboardListSkeleton({ rows = 5 }) {
  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1, ...wave }} />
      ))}
    </Paper>
  );
}
